/** EMPLOYEE ONBOARDING — shared presentational parts. */
import { Check, Circle, Loader2, Package, Warehouse, ShoppingCart, CalendarClock, Wrench, X } from "lucide-react";

export const btnP = "h-9 px-4 rounded-full bg-[color:var(--st-accent)] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-50";
export const btnS = "h-8 px-3 rounded-full border border-[color:var(--st-border)] bg-white text-[12px] font-medium inline-flex items-center gap-1.5 hover:border-[color:var(--st-border-strong)] disabled:opacity-50";
export const inp = "h-9 w-full rounded-lg border border-[color:var(--st-border)] bg-white px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[color:var(--st-accent-soft)]";
export const d10 = (v) => (v ? String(v).slice(0, 10) : "—");
export const fmtDT = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export function Field({ label, children, required, testId }) {
  return (
    <label className="block text-[12px]" data-testid={testId}>
      <span className="text-[color:var(--st-text-muted)] font-medium">{label}{required && <span className="text-[color:var(--st-critical)]"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const DOT = { done: "bg-[color:var(--st-success)] text-white", current: "bg-[color:var(--st-accent)] text-white ring-4 ring-[color:var(--st-accent-soft)]", pending: "bg-white border border-[color:var(--st-border-strong)] text-[color:var(--st-text-subtle)]", cancelled: "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-subtle)]" };

/** Horizontal 10-stage tracker (compact) — used in list rows and detail header. */
export function StageTracker({ stages, compact = false, testId }) {
  return (
    <ol className={`flex items-start ${compact ? "gap-0" : "gap-0"} w-full overflow-x-auto`} data-testid={testId}>
      {stages.map((s, i) => (
        <li key={s.key} className="flex-1 min-w-[56px] flex flex-col items-center relative" data-testid={`${testId}-${s.key}`} data-state={s.state}>
          {i > 0 && <span className={`absolute left-[-50%] right-[50%] top-[11px] h-[2px] ${s.state === "done" || s.state === "current" ? "bg-[color:var(--st-success)]" : "bg-[color:var(--st-border)]"}`} />}
          <span className={`relative z-10 h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold ${DOT[s.state] || DOT.pending}`}>
            {s.state === "done" ? <Check className="h-3.5 w-3.5" /> : s.state === "cancelled" ? <X className="h-3 w-3" /> : s.state === "current" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Circle className="h-2 w-2" />}
          </span>
          {!compact && <span className={`mt-1.5 text-[10px] leading-tight text-center ${s.state === "current" ? "font-semibold text-[color:var(--st-text)]" : "text-[color:var(--st-text-muted)]"}`}>{s.label}</span>}
        </li>
      ))}
    </ol>
  );
}

export function ProgressBar({ pct, testId }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-[color:var(--st-surface-alt)] overflow-hidden" data-testid={testId}>
      <div className="h-full rounded-full bg-[color:var(--st-success)] transition-[width] duration-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Availability card for one equipment package. */
export function PackageCard({ pkg, selected, onSelect, index = 0 }) {
  const p = pkg.plan;
  const tone = p.in_stock ? "text-[color:var(--st-success)] bg-[color:var(--st-success-soft)]" : "text-[color:var(--st-warn)] bg-[color:var(--st-warn-soft)]";
  return (
    <button type="button" onClick={onSelect} data-testid={`pkg-card-${pkg.id}`} aria-pressed={selected} style={{ animationDelay: `${index * 50}ms` }}
      className={`os-card os-rise text-left p-4 flex flex-col gap-3 w-full ${selected ? "ring-2 ring-[color:var(--st-accent)]" : ""}`}>
      <div className="flex items-start gap-2">
        <span className="h-9 w-9 rounded-lg grid place-items-center bg-[color:var(--st-surface-alt)]"><Package className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0"><div className="text-[14px] font-semibold">{pkg.name}</div><div className="text-[11px] text-[color:var(--st-text-muted)]">{pkg.role_tag || "General"} · {pkg.items?.length || 0} item(s)</div></div>
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${tone}`} data-testid={`pkg-card-${pkg.id}-stock`}>{p.in_stock ? "In stock" : "Procurement"}</span>
      </div>
      <ul className="text-[12px] space-y-1">
        {p.lines.map((l) => (
          <li key={l.product_name} className="flex items-center gap-2">
            <span className={`h-1.5 w-1.5 rounded-full ${l.source === "procurement" ? (l.optional ? "bg-[color:var(--st-border-strong)]" : "bg-[color:var(--st-warn)]") : "bg-[color:var(--st-success)]"}`} />
            <span className="flex-1 truncate">{l.product_name}{l.optional ? <span className="text-[color:var(--st-text-subtle)]"> · optional</span> : ""}</span>
            <span className="font-mono text-[11px] text-[color:var(--st-text-muted)]">×{l.qty} · {l.from_buffer + l.from_warehouse}/{l.qty}</span>
          </li>))}
      </ul>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Mini icon={Warehouse} k="Available stock" v={`${p.available_stock}/${p.required_units}`} />
        <Mini icon={Warehouse} k="Warehouse" v={p.primary_warehouse || "—"} />
        <Mini icon={ShoppingCart} k="Procurement" v={p.procurement_required ? `${p.procurement_required} unit(s)` : "Not required"} warn={!!p.procurement_required} />
        <Mini icon={CalendarClock} k="Expected delivery" v={p.expected_delivery} />
      </div>
      <div className="text-[11px] text-[color:var(--st-text-muted)] flex items-start gap-1.5"><Wrench className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{p.configuration.slice(0, 3).join(" · ")}{p.configuration.length > 3 ? ` +${p.configuration.length - 3}` : ""}</span></div>
    </button>
  );
}

function Mini({ icon: I, k, v, warn }) {
  return (
    <div className="rounded-lg bg-[color:var(--st-surface-alt)] px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-[color:var(--st-text-subtle)] flex items-center gap-1"><I className="h-3 w-3" />{k}</div>
      <div className={`font-semibold truncate ${warn ? "text-[color:var(--st-warn)]" : ""}`}>{v}</div>
    </div>
  );
}

export function KV({ k, v, testId }) {
  return <div className="flex justify-between gap-3 py-1 text-[12px] border-b border-dashed border-[color:var(--st-border)] last:border-0"><span className="text-[color:var(--st-text-muted)]">{k}</span><span className="font-medium text-right break-words max-w-[65%]" data-testid={testId}>{v || "—"}</span></div>;
}
